import {
    Box,
    Button,
    Dialog,
    DialogProps,
    Divider,
    Grid,
} from "@material-ui/core";
import { Championship, RaceControl, useChampionshipDrivers } from "api";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React, { useContext, useMemo } from "react";
import {
    InputFieldFormik,
    LoggedUserContext,
    SubmitButton,
    Title,
    yupMaxLengthField,
    yupMinLengthField,
    yupMinValueField,
    yupRequiredField,
} from "ui/componentes";
import {
    ProtestDefaultValues,
    ProtestInfractionSelection,
    sendProtest,
} from "ui/views/Protests";
import { ProtestedDriverSelection } from "ui/views/Protests/ProtestedDriverSelection";
import * as Yup from "yup";

const cancelForm = (resetForm, onClose) => () => {
    resetForm();
    onClose();
};

const ProtestFormValidation = Yup.object().shape({
    lap: Yup.number().required(yupRequiredField).min(0, yupMinValueField),
    infractionId: Yup.string().required(yupRequiredField),
    complaint: Yup.string()
        .required(yupRequiredField)
        .min(50, yupMinLengthField)
        .max(300, yupMaxLengthField),
    protestedDriverId: Yup.string().required(yupRequiredField),
});

interface IncidentReportProtestProps {
    championship: Championship;
    raceControl: RaceControl;
}

const IncidentReportProtest: React.FC<
    IncidentReportProtestProps & DialogProps
> = ({ open = false, onClose, championship, raceControl }) => {
    const { enqueueSnackbar } = useSnackbar();
    const loggedUser = useContext(LoggedUserContext);
    const drivers = useChampionshipDrivers(championship?.id);
    const driversWithoutLoggedUser = useMemo(
        () => (drivers || []).filter((d) => d.userId !== loggedUser.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [drivers]
    );
    const hasRaces = championship.raceConfigurations?.length > 1;

    return (
        <Formik
            enableReinitialize={true}
            initialValues={ProtestDefaultValues}
            validationSchema={ProtestFormValidation}
            onSubmit={(protest, { setSubmitting, resetForm }) => {
                protest = {
                    ...protest,
                    seasonId: championship.seasonId || "",
                    championshipId: championship.id || "",
                    roundId: raceControl.roundId || "",
                    raceControlId: raceControl.id || "",
                };
                sendProtest(
                    protest,
                    setSubmitting,
                    resetForm,
                    enqueueSnackbar
                ).then(() => {
                    if (onClose) {
                        onClose({}, "escapeKeyDown");
                    }
                });
            }}
        >
            {({ resetForm, values, isSubmitting }) => {
                return (
                    <Dialog
                        open={open}
                        onClose={(event, reason) => {
                            resetForm();
                            if (onClose) {
                                onClose(event, reason);
                            }
                        }}
                    >
                        <Form>
                            <Box p={3}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Title
                                            text={`Abertura de protesto ${
                                                hasRaces
                                                    ? `para Bateria ${raceControl.raceNumber}`
                                                    : ""
                                            }`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <InputFieldFormik
                                            required
                                            id="lap"
                                            name="lap"
                                            label="Volta"
                                            type="number"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputFieldFormik
                                            required
                                            id="complaint"
                                            name="complaint"
                                            label="Descrição do incidente"
                                            multiline
                                            rows={6}
                                            helperText={`Max.: ${values.complaint.length} / 300`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ProtestInfractionSelection
                                            seasonId={championship.seasonId}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <ProtestedDriverSelection
                                            drivers={driversWithoutLoggedUser}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            fullWidth
                                            disabled={isSubmitting}
                                            onClick={cancelForm(
                                                resetForm,
                                                onClose
                                            )}
                                        >
                                            Fechar
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <SubmitButton fullWidth>
                                            {isSubmitting
                                                ? "Enviando protesto"
                                                : "Enviar protesto"}
                                        </SubmitButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Form>
                    </Dialog>
                );
            }}
        </Formik>
    );
};

export { IncidentReportProtest };
